import NiceModal from '@ebay/nice-modal-react';
import { createFileRoute, notFound, useRouter } from '@tanstack/react-router';
import dayjs from 'dayjs';
import { useAtom, useSetAtom } from 'jotai';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { cancelTransitionMySell, getMySellInfo } from '@/api/general';
import {
  countDownSecondsAtom,
  deadlineAtom,
  pollingIntervalIdAtom,
  servicesUrlAtom,
  statusAtom,
  transitionIdAtom,
} from '@/atoms/general';
import CardHeader from '@/components/card-header';
import ContactButton from '@/components/contact-button';
import HintSection from '@/components/hint-section';
import OrderNumberSection from '@/components/order-number-section';
import PageLoadingSpinner from '@/components/page-loading-spinner';
import RemarkSection from '@/components/remark-section';
import TransferDetailMysell from '@/components/transfer-detail-mysell';
import TransitionAmountSection from '@/components/transition-amount-section';
import { POLLING_INTERVAL } from '@/config';
import { DEPOSIT_CATEGORY, DEPOSIT_METHOD, MY_SELL, PAYMENT_TYPE, type PaymentCategory } from '@/constants/general';
import { MY_SELL_STATUS } from '@/constants/status';
import { cn } from '@/lib/utils';
import { formatCurrency } from '@/lib/utils';

export const Route = createFileRoute('/_layout/mysell/$id')({
  loader: async ({ params: { id } }) => {
    const paymentInfo = await getMySellInfo(id);
    if (!paymentInfo) throw notFound();

    return paymentInfo;
  },
  pendingComponent: () => <PageLoadingSpinner />,
  component: MySell,
});

function MySell() {
  const paymentInfo = Route.useLoaderData();
  const { id } = Route.useParams();
  const router = useRouter();
  const { t } = useTranslation();
  const [pollingIntervalId, setPollingIntervalId] = useAtom(pollingIntervalIdAtom);
  const setTransitionId = useSetAtom(transitionIdAtom);
  const setServicesUrl = useSetAtom(servicesUrlAtom);
  const setCountDownSeconds = useSetAtom(countDownSecondsAtom);
  const [status, setStatus] = useAtom(statusAtom);
  const [deadline, setDeadline] = useAtom(deadlineAtom);

  const [paymentCategory, setPaymentCategory] = useState<PaymentCategory | ''>('');
  const [isQrPayment, setIsQrPayment] = useState<boolean>(false);

  useEffect(() => {
    setTransitionId(id);
    setServicesUrl(paymentInfo.merchant_cs_url);
    setPaymentCategory(paymentInfo.app_code.split('_')[0] as PaymentCategory);

    const method = paymentInfo.app_code.split('_')[1];
    setIsQrPayment(method === DEPOSIT_METHOD.QR || method === DEPOSIT_METHOD.QR_TRANSFER);
  }, [id, paymentInfo]);

  useEffect(() => {
    const deadline = dayjs(paymentInfo.transfer_deadline);
    const calculatedCountDownSeconds = Math.floor((deadline.toDate().getTime() - new Date().getTime()) / 1000);

    setCountDownSeconds(calculatedCountDownSeconds);
    setDeadline(deadline);

    if (calculatedCountDownSeconds <= 0 && paymentInfo.web_status === MY_SELL_STATUS.PENDING_TRANSFER) {
      setStatus(MY_SELL_STATUS.TIMEOUT);
    } else {
      setStatus(paymentInfo.web_status);

      if (paymentInfo.web_status === MY_SELL_STATUS.PENDING_TRANSFER && calculatedCountDownSeconds > 0) {
        if (paymentCategory === DEPOSIT_CATEGORY.ECNY)
          NiceModal.show('ecny-warning-modal', { amount: formatCurrency(paymentInfo.amount) });
        if (paymentCategory === DEPOSIT_CATEGORY.ALIPAY) NiceModal.show('alipay-warning-modal');
      }
    }
  }, [paymentInfo, paymentCategory]);

  useEffect(() => {
    if (pollingIntervalId) return;

    const intervalId = setInterval(() => {
      router.invalidate();
    }, POLLING_INTERVAL);

    setPollingIntervalId(intervalId);

    return () => {
      clearInterval(intervalId);
      setPollingIntervalId(null);
    };
  }, [router, setPollingIntervalId]);

  return (
    <>
      <main className="flex items-center main-height justify-center p-3 sm:p-6 md:p-10 bg-main-gray overflow-y-scroll relative">
        <div className={cn('device-height w-[340px] bg-main-white rounded-xl shadow-custom relative flex flex-col')}>
          <ContactButton />
          <CardHeader />
          <div className="flex flex-col overflow-y-scroll flex-1">
            <div className="flex flex-col h-full">
              <OrderNumberSection orderId={paymentInfo?.merchant_order_id} createdTime={paymentInfo?.created_time} />
              <TransitionAmountSection amount={paymentInfo?.amount} />
              <TransferDetailMysell
                qrContent={paymentInfo?.is_qr ? paymentInfo?.qr_code : undefined}
                status={status}
                deadline={deadline}
                cardHolder={paymentInfo?.card_holder}
                cardNumber={paymentInfo?.card_number}
                bankName={paymentInfo?.bank_name}
                ifscCode={paymentInfo?.ifsc_code}
                paymentCategory={paymentCategory}
              />
              <HintSection
                type={
                  paymentCategory === DEPOSIT_CATEGORY.ECNY
                    ? isQrPayment
                      ? 'digital-cny-scan'
                      : 'digital-cny'
                    : 'general'
                }
              />
              {paymentInfo?.merchant_remark && <RemarkSection remark={paymentInfo?.merchant_remark} />}
            </div>
          </div>
          {status === MY_SELL_STATUS.PENDING_TRANSFER && (
            <div className="py-3 border-t flex flex-col -bottom-[3.3rem] border-border-gray bg-main-white rounded-b-xl">
              <div
                className={cn('flex divide-x divide-border-gray', paymentCategory === DEPOSIT_CATEGORY.ECNY && 'pb-3')}
              >
                <button
                  onClick={() => {
                    NiceModal.show('transition-cancel-modal', {
                      title: t('btn_order_cancel_dialog'),
                      onConfirm: () => {
                        setStatus(MY_SELL_STATUS.CANCELLING);
                        cancelTransitionMySell(paymentInfo.merchant_order_id);
                        router.invalidate();
                      },
                    });
                  }}
                  className="flex-1 cursor-pointer text-[#5B78A1] hover:text-button-active bg-main-white transition-colors text-sm text-center"
                >
                  {t('btn_order_cancel')}
                </button>
                <button
                  onClick={() =>
                    NiceModal.show('transition-image-upload-modal', {
                      id: paymentInfo.merchant_order_id,
                      onConfirm: () => router.invalidate(),
                      paymentType: PAYMENT_TYPE.MY_SELL,
                    })
                  }
                  className="flex-1 cursor-pointer text-button-inactive hover:text-button-active bg-main-white transition-colors text-sm font-semibold text-center"
                >
                  {t('btn_transfer_completed')}
                </button>
              </div>
              {paymentCategory === DEPOSIT_CATEGORY.ECNY && (
                <button
                  onClick={() => NiceModal.show('ecny-tutorial-modal')}
                  className="flex-1 pt-3 pb-1 cursor-pointer text-[#5B78A1] border-t border-border-gray bg-main-white hover:text-button-active transition-colors text-sm text-center"
                >
                  {t('btn_digital_cny_tran_guide')}
                </button>
              )}
            </div>
          )}
          {status === MY_SELL_STATUS.TIMEOUT && (
            <section className="py-4 border-t flex divide-x divide-border-gray z-0 -bottom-[3.3rem] border-border-gray bg-main-white rounded-b-xl">
              <button
                onClick={() => {
                  NiceModal.show('transition-cancel-modal', {
                    title: t('btn_order_cancel_dialog'),
                    onConfirm: () => {
                      setStatus(MY_SELL_STATUS.CANCELLING);
                      cancelTransitionMySell(paymentInfo.merchant_order_id);
                      router.invalidate();
                    },
                  });
                }}
                className="flex-1 cursor-pointer text-[#5B78A1] hover:text-button-active bg-main-white transition-colors text-sm text-center"
              >
                {t('btn_order_cancel')}
              </button>
            </section>
          )}
        </div>
      </main>
    </>
  );
}
